import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { Download, Search, TableView } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PageHeaderLayout from '../../../components/Layout/PageHeaderLayout';
import { ReportServices } from '../ReportServices';
import WeeklyReportDocument, { weeklySummaryMetrics } from './WeeklyReportDocument';

const getLastWeekRange = () => {
  const today = dayjs();
  const thisMonday = today.subtract((today.day() + 6) % 7, 'day');
  return {
    startDate: thisMonday.subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: thisMonday.subtract(1, 'day').format('YYYY-MM-DD'),
  };
};

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const WeeklyOperationsReport = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const reportRef = useRef(null);

  const { control, handleSubmit, getValues, setValue, formState: { errors } } = useForm({
    defaultValues: getLastWeekRange(),
  });

  useEffect(() => {
    dispatch({ type: 'menuSelect', payload: 'weekly-operations-report' });
  }, [dispatch]);

  const fetchReport = useCallback(async (values) => {
    setLoading(true);
    setError('');
    try {
      const response = await ReportServices.fetchWeeklyOperationsReport(values);
      if (response?.success === 1) {
        setReport(response.data);
      } else {
        setReport(null);
        setError(response?.message || 'Unable to load the weekly report.');
      }
    } catch (requestError) {
      setReport(null);
      setError(requestError?.message || 'Unable to load the weekly report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(getValues());
  }, [fetchReport, getValues]);

  const periodLabel = useMemo(() => {
    if (!report?.period) return '';
    return `${dayjs(report.period.startDate).format('DD MMM YYYY')} – ${dayjs(report.period.endDate).format('DD MMM YYYY')}`;
  }, [report]);

  const downloadCsv = () => {
    if (!report) return;

    const summary = report.summary || {};
    const rows = [
      ['CuDel Weekly Operations Report'],
      ['Period', periodLabel],
      ['Timezone', report.period?.timezone || 'Asia/Kolkata'],
      [],
      ['Summary'],
      ['Metric', 'Count'],
      ...weeklySummaryMetrics.map(([key, label]) => [label, summary[key] || 0]),
      [],
      ['Category & Geography Wise Expert Count'],
      ['Category', 'Geography', 'Total Experts', 'Active Experts', 'Executed Bookings', 'Missed Bookings'],
      ...(report.categoryGeography || []).map((row) => [
        row.category,
        row.geography,
        row.totalExperts,
        row.activeExperts,
        row.executedBookings,
        row.missedBookings,
      ]),
      [],
      ['Top Performing Experts'],
      ['Rank', 'Expert', 'Category', 'Geography', 'Executed', 'Missed', 'Weekly Earnings'],
      ...(report.topPerformingExperts || []).map((row) => [
        row.rank,
        row.expertName,
        row.category,
        row.geography,
        row.executedBookings,
        row.missedBookings,
        row.weeklyEarnings,
      ]),
      [],
      ['Earnings by Category'],
      ['Category', 'Weekly Earnings'],
      ...(report.earningsByCategory || []).map((row) => [row.category, row.weeklyEarnings]),
      [],
      ['Earnings by Geography'],
      ['Geography', 'Weekly Earnings'],
      ...(report.earningsByGeography || []).map((row) => [row.geography, row.weeklyEarnings]),
      [],
      ['Expert Services Added by City'],
      ['City', 'Services Added', 'Experts Adding Services'],
      ...(report.servicesByCity || []).map((row) => [
        row.city,
        row.servicesAdded,
        row.expertsAddingServices,
      ]),
      ['Total Services Added', summary.expertServicesAdded || 0],
      [],
      ['Bookings by City'],
      ['City', 'Total Bookings', 'Completed', 'Admin Panel', 'App / Customer'],
      ...(report.bookingsByCity || []).map((row) => [
        row.city,
        row.totalBookings,
        row.completedBookings,
        row.adminPanelBookings,
        row.customerAppBookings,
      ]),
    ];

    const csv = `\uFEFF${rows.map((row) => row.map(csvEscape).join(',')).join('\n')}`;
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `cudel-weekly-report-${report.period.startDate}-to-${report.period.endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    if (!reportRef.current || !report) return;

    setDownloading(true);
    setError('');
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const reportWidth = reportRef.current.scrollWidth;
      const reportHeight = reportRef.current.scrollHeight;
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: reportWidth,
        height: reportHeight,
        windowWidth: Math.max(1200, reportWidth),
        scrollX: 0,
        onclone: (clonedDocument) => {
          const clonedReport = clonedDocument.querySelector('[data-weekly-report-document="true"]');
          if (!clonedReport) return;

          clonedReport.style.setProperty('width', `${reportWidth}px`, 'important');
          clonedReport.style.setProperty('min-width', `${reportWidth}px`, 'important');
          clonedReport.style.setProperty('max-width', 'none', 'important');
          clonedReport.style.setProperty('margin', '0', 'important');
          clonedReport.style.setProperty('transform', 'none', 'important');

          clonedReport.querySelectorAll('th, td').forEach((cell) => {
            cell.style.setProperty('text-align', 'center', 'important');
            cell.style.setProperty('vertical-align', 'middle', 'important');
          });

          // html2canvas can ignore percentage transforms used for visual
          // centering. Measure each rendered line box and use an exact pixel
          // offset in the cloned document instead.
          const centeredValueMeasurements = Array.from(
            clonedReport.querySelectorAll('[data-pdf-centered-value="true"]'),
          ).map((value) => {
            const cell = value.closest('[data-pdf-centered-cell="true"]');
            return {
              value,
              cell,
              cellHeight: cell?.getBoundingClientRect().height || 0,
              valueHeight: value.getBoundingClientRect().height || 0,
            };
          });

          centeredValueMeasurements.forEach(({ value, cell, cellHeight, valueHeight }) => {
            const fallbackTop = Number(value.getAttribute('data-pdf-top') || 8);
            const measuredTop = Math.max(1, Math.round((cellHeight - valueHeight) / 2) - 1);
            const top = cellHeight && valueHeight ? measuredTop : fallbackTop;

            if (cell) {
              cell.style.setProperty('position', 'relative', 'important');
              cell.style.setProperty('height', `${cellHeight}px`, 'important');
              cell.style.setProperty('min-height', `${cellHeight}px`, 'important');
            }
            value.style.setProperty('position', 'absolute', 'important');
            value.style.setProperty('top', `${top}px`, 'important');
            value.style.setProperty('left', '0', 'important');
            value.style.setProperty('width', '100%', 'important');
            value.style.setProperty('padding-left', '8px', 'important');
            value.style.setProperty('padding-right', '8px', 'important');
            value.style.setProperty('box-sizing', 'border-box', 'important');
            value.style.setProperty('line-height', '1.25', 'important');
            value.style.setProperty('white-space', 'normal', 'important');
            value.style.setProperty('overflow', 'visible', 'important');
            value.style.setProperty('overflow-wrap', 'anywhere', 'important');
            value.style.setProperty('text-align', 'center', 'important');
            value.style.setProperty('transform', 'none', 'important');
          });

          const tableValueMeasurements = Array.from(
            clonedReport.querySelectorAll('[data-pdf-table-value="true"]'),
          ).map((value) => {
            const cell = value.closest('th, td');
            const computedCell = cell ? clonedDocument.defaultView?.getComputedStyle(cell) : null;
            return {
              value,
              cell,
              cellHeight: cell?.getBoundingClientRect().height || 0,
              valueHeight: value.getBoundingClientRect().height || 0,
              paddingLeft: computedCell?.paddingLeft || '8px',
              paddingRight: computedCell?.paddingRight || '8px',
            };
          });

          tableValueMeasurements.forEach(({
            value,
            cell,
            cellHeight,
            valueHeight,
            paddingLeft,
            paddingRight,
          }) => {
            const top = Math.max(1, Math.round((cellHeight - valueHeight) / 2) - 1);

            if (cell) {
              cell.style.setProperty('position', 'relative', 'important');
              cell.style.setProperty('box-sizing', 'border-box', 'important');
              cell.style.setProperty('height', `${cellHeight}px`, 'important');
              cell.style.setProperty('padding', '0', 'important');
              cell.style.setProperty('overflow', 'visible', 'important');
            }
            value.style.setProperty('display', 'block', 'important');
            value.style.setProperty('position', 'absolute', 'important');
            value.style.setProperty('top', `${top}px`, 'important');
            value.style.setProperty('left', '0', 'important');
            value.style.setProperty('width', '100%', 'important');
            value.style.setProperty('padding-left', paddingLeft, 'important');
            value.style.setProperty('padding-right', paddingRight, 'important');
            value.style.setProperty('box-sizing', 'border-box', 'important');
            value.style.setProperty('margin-top', '0', 'important');
            value.style.setProperty('line-height', '1.3', 'important');
            value.style.setProperty('white-space', 'normal', 'important');
            value.style.setProperty('overflow', 'visible', 'important');
            value.style.setProperty('overflow-wrap', 'anywhere', 'important');
            value.style.setProperty('text-align', 'center', 'important');
            value.style.setProperty('transform', 'none', 'important');
          });
        },
      });

      const margin = 7;
      const pageWidth = 210;
      const printableWidth = pageWidth - (margin * 2);
      const imageHeight = (canvas.height * printableWidth) / canvas.width;
      const pageHeight = Math.max(297, imageHeight + (margin * 2));
      const imageData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pageWidth, pageHeight],
        compress: true,
      });

      // Keep the email-style report on one continuous PDF page. Splitting the
      // same screenshot over A4 pages cuts through table rows and duplicates
      // borders at page boundaries.
      pdf.addImage(imageData, 'JPEG', margin, margin, printableWidth, imageHeight, undefined, 'FAST');

      pdf.save(`cudel-weekly-report-${report.period.startDate}-to-${report.period.endDate}.pdf`);
    } catch (downloadError) {
      setError(downloadError?.message || 'Unable to download the PDF report.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <PageHeaderLayout headerTitle="CuDel Weekly Operations Report">
        <Button
          variant="outlined"
          size="small"
          startIcon={<TableView />}
          disabled={!report || loading || downloading}
          onClick={downloadCsv}
        >
          Export CSV
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
          disabled={!report || loading || downloading}
          onClick={downloadPdf}
        >
          {downloading ? 'Preparing PDF' : 'Download Report'}
        </Button>
      </PageHeaderLayout>

      <Paper
        component="form"
        onSubmit={handleSubmit(fetchReport)}
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={5} md={3}>
            <Controller
              name="startDate"
              control={control}
              rules={{ required: 'Start date is required' }}
              render={({ field }) => (
                <DatePicker
                  label="Start date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => {
                    const startDate = value?.isValid() ? value.format('YYYY-MM-DD') : '';
                    field.onChange(startDate);
                    setValue(
                      'endDate',
                      startDate ? value.add(6, 'day').format('YYYY-MM-DD') : '',
                      { shouldDirty: true, shouldValidate: true },
                    );
                  }}
                  slotProps={{
                    textField: {
                      name: field.name,
                      inputRef: field.ref,
                      onBlur: field.onBlur,
                      size: 'small',
                      fullWidth: true,
                      error: Boolean(errors.startDate),
                      helperText: errors.startDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={3}>
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: 'End date is required',
                validate: (value) => value >= getValues('startDate') || 'End date must be after start date',
              }}
              render={({ field }) => (
                <DatePicker
                  label="End date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(value) => field.onChange(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      name: field.name,
                      inputRef: field.ref,
                      onBlur: field.onBlur,
                      size: 'small',
                      fullWidth: true,
                      error: Boolean(errors.endDate),
                      helperText: errors.endDate?.message,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2} md="auto">
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              disabled={loading}
              fullWidth
            >
              Generate
            </Button>
          </Grid>
          {periodLabel && (
            <Grid item xs={12} md>
              <Typography color="text.secondary" sx={{ textAlign: { xs: 'left', md: 'right' }, fontSize: '.8rem' }}>
                Reporting period: {periodLabel} (IST)
              </Typography>
            </Grid>
          )}
        </Grid>
        </LocalizationProvider>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {report && (
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 1.5 }}>
          <WeeklyReportDocument ref={reportRef} report={report} />
        </Box>
      )}
    </Box>
  );
};

export default WeeklyOperationsReport;
